/**
 * 납품계 작성/수정 폼 모달.
 *
 *   [작성]
 *     1. 분할납품요구서 PDF 업로드 → 자동 추출(사업명·수요기관·금액·날짜·품목 등)
 *     2. 제출일(시행일) 입력(필수) → 문서번호 자동 제안 / 준공일(선택)
 *     3. 자동 채움 필드 검토·수정 + 직인 포함 여부 + (선택) 사진대지 업로드(PDF/PPT)
 *     4. [생성] → 5종 양식 PDF 생성 → 미리보기
 *   [수정] editId 지정 시 기존 값 불러와 수정 → 저장.
 *
 *   날짜 입력은 영업건과 동일한 DateInput — "6-1" 처럼 숫자로 치고 Enter 면 자동으로 날짜 완성.
 */

import { useCallback, useEffect, useState } from 'react';
import { DraggableModal } from '../../components/DraggableModal';
import { DateInput } from '../../components/DateInput';
import { useDirtyClose } from '../../lib/useDirtyClose';
import {
  parseBunapFile, getNextDocNo, createDelivery, getDelivery, updateDelivery,
  type DeliveryCreateInput, type DeliveryUpdateInput,
} from '../../lib/delivery-api';
import {
  AttachmentListEditor, NAPUM_DEFAULT_ATTACHMENTS, withKeys, type DocAttachment,
} from './AttachmentListEditor';
import { LoadingLines } from '../../components/ui/Skeleton';

interface Props {
  /** 지정 시 수정 모드 (해당 납품계를 불러와 편집) */
  editId?: number | null;
  onClose: () => void;
  onSaved: (id: number, docNo: string) => void;
}

interface FormState {
  businessName: string;
  recipient: string;
  department: string;
  procurementNo: string;
  contractDate: string;
  deliveryDue: string;
  amount: string;
  itemName: string;
  spec: string;
  unit: string;
  qty: string;
}

const EMPTY: FormState = {
  businessName: '', recipient: '', department: '', procurementNo: '',
  contractDate: '', deliveryDue: '', amount: '', itemName: '', spec: '', unit: '대', qty: '1',
};

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/; // 년-월까지만 (DateInput allowMonthOnly · v2.322.0)

export function DeliveryStatementFormModal({ editId, onClose, onSaved }: Props) {
  // 작성 중 닫힘 방지 — ESC·[×]·[취소] 는 전부 requestClose 를 거친다 (v2.163.0)
  const { markDirty, requestClose } = useDirtyClose(onClose);
  const isEdit = editId != null;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [bunapFile, setBunapFile] = useState<File | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  // 제출일(시행일) — 문서 날짜·문서번호 기준 (필수). 준공일 — 준공년월일 (선택, 비우면 공란).
  const [submitDate, setSubmitDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  // 준공일을 사용자가 직접 건드렸는지 — 제출일 입력 시 자동채움 여부 판단(직접 입력이면 유지).
  const [completionTouched, setCompletionTouched] = useState(false);
  const [includeSeal, setIncludeSeal] = useState(true);
  const [attachments, setAttachments] = useState<DocAttachment[]>(NAPUM_DEFAULT_ATTACHMENTS);
  const [docNo, setDocNo] = useState('');
  const [docNoTouched, setDocNoTouched] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [parsedNote, setParsedNote] = useState('');

  const set = (k: keyof FormState) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // 수정 모드 — 기존 값 불러오기
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const d = await getDelivery(editId!);
        if (!alive) return;
        setForm({
          businessName: d.businessName ?? '',
          recipient: d.recipient ?? '',
          department: d.department ?? '',
          procurementNo: d.procurementNo ?? '',
          contractDate: d.contractDate ?? '',
          deliveryDue: d.deliveryDue ?? '',
          amount: d.amount != null ? String(d.amount) : '',
          itemName: d.itemName ?? '',
          spec: d.spec ?? '',
          unit: d.unit ?? '대',
          qty: d.qty != null ? String(d.qty) : '1',
        });
        setSubmitDate(d.issueDate ?? '');
        setCompletionDate(d.completionDate ?? ''); // 비어있으면 준공년월일 공란
        setCompletionTouched(true); // 수정 모드에선 기존 준공일 유지(자동채움 끔)
        setIncludeSeal(d.includeSeal !== false);
        {
          const baseAtt = d.attachments && d.attachments.length > 0
            ? withKeys(d.attachments, NAPUM_DEFAULT_ATTACHMENTS)   // 옛 자료에 표식 되붙이기 (v2.179.0)
            : NAPUM_DEFAULT_ATTACHMENTS;
          setAttachments(baseAtt.map((a, i) => {
            const ef = d.extraFiles?.find((f) => f.sort === i);
            return ef ? { ...a, existingFileId: ef.id, existingFileName: ef.filename } : a;
          }));
        }
        setDocNo(d.docNo ?? '');
        setDocNoTouched(true); // 수정 모드에선 기존 문서번호 유지(자동 제안 끔)
      } catch (e) {
        if (alive) setError(`불러오기 실패: ${(e as Error).message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isEdit, editId]);

  const onPickBunap = useCallback(async (file: File | null) => {
    setBunapFile(file);
    setError('');
    setParsedNote('');
    if (!file) return;
    setParsing(true);
    try {
      const r = await parseBunapFile(file);
      setForm((f) => ({
        ...f,
        businessName: r.businessName ?? f.businessName,
        recipient: r.endUser ?? f.recipient,
        department: r.department ?? f.department,
        procurementNo: r.procurementNo ?? f.procurementNo,
        contractDate: r.requestDate ?? f.contractDate,
        deliveryDue: r.deliveryDue ?? f.deliveryDue,
        amount: r.totalAmount != null ? String(r.totalAmount) : f.amount,
        itemName: r.itemName ?? f.itemName,
        spec: r.spec ?? f.spec,
        unit: r.unit ?? f.unit,
        qty: r.qty != null ? String(r.qty) : f.qty,
      }));
      setParsedNote('분납서에서 자동으로 채웠습니다. 내용을 확인·수정해 주세요.');
    } catch (e) {
      setError(`분납서 자동 추출 실패: ${(e as Error).message} (직접 입력 가능)`);
    } finally {
      setParsing(false);
    }
  }, []);

  // 제출일 입력 시 ① 문서번호 자동 제안 ② 준공일 자동 채움 (각각 사용자가 직접 건드리지 않은 경우만)
  const onChangeSubmitDate = useCallback(async (v: string) => {
    setSubmitDate(v);
    // 년-월까지만(2026-11) 적어도 받는다 (v2.322.0 · 개발요청 #32) — 준공일도 같은 모양(월까지만)으로 채워진다
    if (v && (ISO_RE.test(v) || MONTH_RE.test(v))) {
      if (!completionTouched) setCompletionDate(v); // 준공일 = 제출일(기본값, 직접 수정 가능)
      if (!docNoTouched) {
        try {
          const { docNo: suggested } = await getNextDocNo(v);
          setDocNo(suggested);
        } catch { /* 제안 실패 시 무시 — 수동 입력 가능 */ }
      }
    }
  }, [docNoTouched, completionTouched]);

  async function submit() {
    setError('');
    if (!isEdit && !bunapFile) { setError('분할납품요구서 PDF 를 업로드해 주세요.'); return; }
    if (!submitDate) { setError('제출일(시행일)을 입력해 주세요.'); return; }
    if (!form.businessName.trim()) { setError('사업명(공사명)을 입력해 주세요.'); return; }
    if (!form.recipient.trim()) { setError('수요기관을 입력해 주세요.'); return; }

    const payload = {
      docNo: docNo || undefined,
      issueDate: submitDate,                 // 제출일(시행일) = 문서 날짜·문서번호 기준
      completionDate: completionDate || '',  // 준공일 — 비우면 준공년월일 공란
      recipient: form.recipient.trim(),
      department: form.department.trim(),
      businessName: form.businessName.trim(),
      procurementNo: form.procurementNo.trim(),
      contractDate: form.contractDate || undefined,
      deliveryDue: form.deliveryDue || '',          // 납품기한 — 비우면 공란(수정 때도 지워진다 · v2.228.1)
      amount: Number(form.amount.replace(/,/g, '')) || 0,
      itemName: form.itemName.trim(),
      spec: form.spec.trim(),
      unit: form.unit.trim() || '대',
      qty: Number(form.qty) || 1,
      includeSeal,
      attachments: attachments.filter((a) => a.name.trim()),
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateDelivery(editId!, payload as DeliveryUpdateInput, photoFile);
        onSaved(editId!, payload.docNo ?? docNo);
      } else {
        const { id, docNo: savedNo } = await createDelivery(payload as DeliveryCreateInput, bunapFile!, photoFile);
        onSaved(id, savedNo);
      }
    } catch (e) {
      setError(`${isEdit ? '수정' : '생성'} 실패: ${(e as Error).message}`);
      setSubmitting(false);
    }
  }

  return (
    <DraggableModal onClose={requestClose} initialWidth={760} closeOnBackdrop={false} closeOnEsc={false} zIndex={60}>
      <div className="dm-drag-handle cursor-move flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        <h2 className="ui-modal-title">{isEdit ? '납품계 수정' : '납품계 작성'}</h2>
        <button type="button" onClick={requestClose} className="ui-close">×</button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm"><LoadingLines /></div>
      ) : (
      <div className="px-6 py-4 space-y-5" onInput={markDirty} onChange={markDirty}>
        {/* 1. 분납서 업로드 (작성 시 필수 / 수정 시엔 원본 유지) */}
        {!isEdit ? (
          <section>
            <h3 className="ui-subsection-title mb-1">① 분할납품요구서 (PDF)</h3>
            <input
              type="file" accept="application/pdf,.pdf"
              onChange={(e) => onPickBunap(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:text-sm hover:file:bg-blue-700"
            />
            {parsing && <p className="text-xs text-blue-600 mt-1">분납서 분석 중…</p>}
            {parsedNote && <p className="text-xs text-emerald-600 mt-1">✓ {parsedNote}</p>}
          </section>
        ) : (
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            수정 모드 — 첨부된 분할납품요구서 원본은 그대로 유지됩니다. 아래 내용·직인·사진대지를 수정할 수 있습니다.
          </p>
        )}

        {/* 2. 제출일(시행일) + 문서번호 + 준공일 */}
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="ui-label block mb-1">② 제출일(시행일) <span className="text-rose-500">*</span></label>
              <DateInput value={submitDate} onChange={onChangeSubmitDate} allowMonthOnly />
              <p className="text-xs text-slate-500 mt-1">문서 날짜·문서번호 기준 · 숫자 「6-1」 입력 후 Enter · 월까지만이면 「2026-11」</p>
            </div>
            <div>
              <label className="ui-label block mb-1">문서번호</label>
              <input type="text" value={docNo} placeholder="예: 2605-SM-007"
                onChange={(e) => { setDocNo(e.target.value); setDocNoTouched(true); }}
                className="ui-input w-full font-mono" />
              <p className="text-xs text-slate-500 mt-1">제출일 입력 시 자동 제안 (수정 가능)</p>
            </div>
          </div>
          <div className="w-1/2 pr-1.5">
            <label className="ui-label block mb-1">준공일 <span className="text-slate-500 font-normal text-xs">(선택)</span></label>
            <DateInput value={completionDate} onChange={(v) => { setCompletionDate(v); setCompletionTouched(true); }} allowMonthOnly />
            <p className="text-xs text-slate-500 mt-1">제출일과 같게 자동 채워집니다 · 다르면 직접 수정 · 비우면 공란 표기</p>
          </div>
        </section>

        {/* 3. 자동 채움 필드 */}
        <section>
          <h3 className="ui-subsection-title mb-2">③ 납품계 내용 {isEdit ? '(수정)' : '(분납서에서 자동 채움 — 확인·수정)'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="사업명(공사명)" value={form.businessName} onChange={set('businessName')} colSpan2 required />
            <Field label="수요기관" value={form.recipient} onChange={set('recipient')} required />
            <Field label="참조 부서" value={form.department} onChange={set('department')} />
            <Field label="납품요구번호" value={form.procurementNo} onChange={set('procurementNo')} mono />
            <DateField label="납품기한" value={form.deliveryDue} onChange={set('deliveryDue')} monthOnly />
            <DateField label="계약·착수일" value={form.contractDate} onChange={set('contractDate')} />
            <Field label="계약금액(품대계)" value={form.amount} onChange={set('amount')} mono />
            <Field label="품명" value={form.itemName} onChange={set('itemName')} />
            <Field label="규격" value={form.spec} onChange={set('spec')} colSpan2 />
            <Field label="단위" value={form.unit} onChange={set('unit')} />
            <Field label="수량" value={form.qty} onChange={set('qty')} mono />
          </div>
        </section>

        {/* 4. 직인 + 사진대지 */}
        <section className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={includeSeal} onChange={(e) => setIncludeSeal(e.target.checked)} />
            <span className="font-semibold">직인(법인 인감) 넣기</span>
            <span className="text-xs text-slate-500">— 공문·납품계·검사원·내역서·보안확약서의 도장</span>
          </label>
          <div>
            <h3 className="ui-subsection-title mb-1">사진대지 <span className="text-slate-400 font-normal">(선택 — PDF 또는 PPT{isEdit ? ', 교체 시에만 업로드' : ', 나중에 추가 가능'})</span></h3>
            <input
              type="file" accept="application/pdf,.pdf,.ppt,.pptx"
              onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-200 file:text-slate-700 file:text-sm hover:file:bg-slate-300"
            />
            {photoFile && <p className="text-xs text-slate-500 mt-1">{photoFile.name}</p>}
          </div>
          <div>
            <h3 className="ui-subsection-title mb-1">붙임서류 <span className="text-slate-500 font-normal text-xs">(표지 공문에 표시 + 만들 서류 고르기 — 줄을 빼면 그 서류가 PDF 에서도 빠집니다)</span></h3>
            <AttachmentListEditor
              items={attachments}
              onChange={setAttachments}
              onReset={() => setAttachments(NAPUM_DEFAULT_ATTACHMENTS)}
            />
          </div>
        </section>

        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
      </div>
      )}

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 sticky bottom-0">
        <button type="button" onClick={requestClose} className="btn btn-secondary">취소</button>
        <button type="button" onClick={submit} disabled={submitting || parsing || loading}
          className="btn btn-primary">
          {submitting ? (isEdit ? '저장 중…' : '생성 중…') : (isEdit ? '수정 저장' : '납품계 생성')}
        </button>
      </div>
    </DraggableModal>
  );
}

function Field({
  label, value, onChange, mono, required, colSpan2,
}: {
  label: string; value: string; onChange: (v: string) => void;
  mono?: boolean; required?: boolean; colSpan2?: boolean;
}) {
  return (
    <div className={colSpan2 ? 'col-span-2' : ''}>
      <label className="ui-label block mb-1">
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-slate-300 rounded px-2 py-1.5 text-sm ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}

// 날짜 필드 — 영업건과 동일한 DateInput("6-1"+Enter 자동완성)
//   monthOnly = 년-월만 받는 칸(납품기한) — 값은 그대로 YYYY-MM-DD 다(그 달 말일)
function DateField({ label, value, onChange, monthOnly }: {
  label: string; value: string; onChange: (v: string) => void; monthOnly?: boolean;
}) {
  return (
    <div>
      <label className="ui-label block mb-1">{label}</label>
      <DateInput value={value} onChange={onChange} allowMonthOnly={monthOnly} />
    </div>
  );
}
